// src/pages/.../BirthdaySection.jsx
import React, { useMemo, useState } from "react";
import { FaBirthdayCake, FaBell } from "react-icons/fa";
import { toast } from "react-toastify";
import { sendNotification } from "../../../services/notificationServices";
import { getUserIdByAdherentId } from "../../../services/adherentServices";

const pickUserId = (u) =>
  u?.UserAdherent?.user_id ?? u?.user_id ?? u?.id ?? null;

const BirthdaySection = ({
  birthdayAdherents = [],
  formatDate,
  calculateAge,
  getDefaultPhoto,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [sendingIds, setSendingIds] = useState(new Set());
  const [userIdsCache, setUserIdsCache] = useState({});

  const hasBirthdays = birthdayAdherents.length > 0;
  const todayLabel = useMemo(() => (formatDate ? formatDate() : ""), [formatDate]);
  const hasToken = !!localStorage.getItem("token");

  const withSending = (id, on = true) =>
    setSendingIds((prev) => {
      const next = new Set(prev);
      on ? next.add(id) : next.delete(id);
      return next;
    });

  const resolveUserIds = async (adherentId) => {
    if (userIdsCache[adherentId]) return userIdsCache[adherentId];
    try {
      const users = await getUserIdByAdherentId(adherentId);
      const ids = (Array.isArray(users) ? users : []).map(pickUserId).filter(Boolean);
      const unique = Array.from(new Set(ids));
      setUserIdsCache((prev) => ({ ...prev, [adherentId]: unique }));
      return unique;
    } catch {
      return [];
    }
  };

  const defaultMessage = (a) =>
    `Nous souhaitons un joyeux anniversaire à ${a?.prenom ?? ""} 🎂`;

  const handleSendOne = async (adherent) => {
    if (!hasToken) {
      toast.error("Vous devez être connecté pour envoyer la notification.");
      return;
    }
    const id = adherent?.id;
    if (!id) return;

    withSending(id, true);
    try {
      const ids = await resolveUserIds(id);
      if (ids.length === 0) {
        toast.info("Aucun destinataire lié à cet adhérent.");
        return;
      }
      const tasks = ids.map((uid) => sendNotification(defaultMessage(adherent), uid));
      const results = await Promise.allSettled(tasks);
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const ko = results.length - ok;
      if (ok && !ko) toast.success("Notification envoyée ✅");
      else if (ok && ko) toast.warn(`Partiel : ${ok} envoyée(s), ${ko} échec(s)`);
      else toast.error("Échec d’envoi de la notification.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’envoi de la notification.");
    } finally {
      withSending(id, false);
    }
  };

  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    if (!s.startsWith("/uploads")) return s;
    const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    return `${base}${s}`;
  };

  return (
    <section className="my-4 rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
      <div className="px-4 sm:px-6 py-2.5 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FaBirthdayCake className="text-xl" />
          <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xl font-semibold bg-black text-white">
            {birthdayAdherents.length}
          </span>
          <h3 className="text-sm sm:text-base font-semibold">Anniversaire(s) : {todayLabel}</h3>
        </div>
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className="rounded-full bg-white/90 text-slate-800 hover:bg-white w-8 h-8 min-h-0 leading-none"
          title={expanded ? "Replier" : "Déployer"}
        >
          {expanded ? "▲" : "▼"}
        </button>
      </div>

      {expanded && (
        <div className="p-4">
          {!hasBirthdays ? (
            <div className="text-center text-slate-600 dark:text-slate-300 py-2">
              Aucun anniversaire aujourd’hui.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {birthdayAdherents.map((a) => {
                const sending = sendingIds.has(a.id);
                const age = calculateAge?.(a.date_naissance);
                const img =
                  toAbsolute(a?.photo_url) || getDefaultPhoto?.(a?.genre) || "/img/judoka_avatar.png";

                return (
                  <div
                    key={a.id}
                    className="relative rounded-xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-slate-200 dark:bg-slate-900"
                  >
                    <div className="p-3 flex flex-col items-center">
                      <img
                        src={img}
                        alt={`${a?.nom ?? ""} ${a?.prenom ?? ""}`}
                        className="w-20 h-20 rounded-full object-cover mb-2 ring-2 ring-white/80"
                      />
                      <p className="text-sm font-semibold text-center">{a?.nom}</p>
                      <p className="text-sm text-center -mt-0.5">{a?.prenom}</p>
                      <div className="mt-2 inline-flex items-center gap-2">
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                          {Number.isFinite(age) ? `${age} ans` : "—"}
                        </span>

                        <button
                          type="button"
                          aria-label="Envoyer une notification d'anniversaire"
                          title={hasToken ? "Notifier" : "Connectez-vous pour notifier"}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendOne(a);
                          }}
                          disabled={sending || !hasToken}
                          className={`shrink-0 rounded-full bg-primary text-white w-8 h-8 flex items-center justify-center shadow transition-transform hover:scale-105 ${
                            sending || !hasToken ? "opacity-70 pointer-events-none" : ""
                          }`}
                        >
                          {sending ? (
                            <span className="loading loading-spinner loading-xs" />
                          ) : (
                            <FaBell className="text-base" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default BirthdaySection;
